import {useQuery} from '@tanstack/react-query';
import {PropsWithChildren, useCallback, useMemo} from 'react';

import {Page} from '../../layout/page/page';
import {LicensesCodeEnum} from '../../page/main/tariffs-and-limits/licenses-block/license/license-const';
import {fetchGptLicenses} from '../../service/neural-network/neural-network-api';
import {GptLicensesListResponseType} from '../../service/neural-network/neural-network-type';
import {useTariffs} from '../../service/tariffs/tariffs-user-tariffs';
import {FeaturesEnum} from '../../service/user/user-type';
import {useDomainConfig} from '../domain-config/domain-config-hook';
import {emptyDomainConfigData} from '../domain-config/domain-data/domain-data-const';
import {useUser} from '../user/user-hook';

import {fetchUserFeatures, fetchUserLicenses} from './license-api';
import {USERS_WITH_REDUCED_FUNCTIONALITY} from './license-const';
import {LicenseContext} from './license-context';
import {getUserLicensesUrl} from './license-provider-helper';
import {LicenseProviderType, LicenseType, LicenseTypeEnum} from './license-provider-type';

export function LicenseProvider(props: PropsWithChildren<Record<string, unknown>>): JSX.Element {
    const {children} = props;
    const {user} = useUser();
    const {isFetching: isTariffsLoading} = useTariffs();
    const {isDomainFeatureEnabled} = useDomainConfig();

    const {data: userFeatures} = useQuery(['user-features', user?.id], () => fetchUserFeatures(), {
        retry: false,
        refetchInterval: false,
        refetchOnWindowFocus: false,
    });

    const {
        data: licenses,
        refetch,
        isInitialLoading,
        isLoading,
    } = useQuery<Array<LicenseType>>(
        [getUserLicensesUrl(), user?.id],
        () => {
            if (user?.id) {
                return fetchUserLicenses();
            }

            return [];
        },
        {
            refetchInterval: false,
            refetchOnWindowFocus: false,
        }
    );

    const {data: licensesGpt, isInitialLoading: isInitialLoadingGpt} = useQuery<GptLicensesListResponseType>(
        [user?.id, user?.isDoubleGisUser],
        () => {
            if (user?.id && user?.isDoubleGisUser) {
                return fetchGptLicenses();
            }

            return [];
        },
        {
            refetchInterval: false,
            refetchOnWindowFocus: false,
        }
    );

    const getLicense = useCallback(
        (licenseCode: LicensesCodeEnum): LicenseType | undefined => {
            return licenses?.find((license) => license.code === licenseCode);
        },
        [licenses]
    );

    // eslint-disable-next-line complexity
    const providerValue: LicenseProviderType = useMemo(() => {
        const features: Array<FeaturesEnum> = userFeatures?.features || [];

        return {
            availableFeatures:
                // !!! Temporary solution, remove this after DEV-18454
                USERS_WITH_REDUCED_FUNCTIONALITY.includes(user?.id || 0)
                    ? emptyDomainConfigData.isDomainFeatureEnabled
                    : isDomainFeatureEnabled,
            enabledFeatures: features,
            licenses: {
                [FeaturesEnum.reviews]: getLicense(LicensesCodeEnum.ReviewLicence),
                [FeaturesEnum.notification]: getLicense(LicensesCodeEnum.NotificationsLicence),
                [FeaturesEnum.reviewAnalyticsBase]:
                    getLicense(LicensesCodeEnum.ReviewAnalyticsLicence)?.type === LicenseTypeEnum.Base ||
                    getLicense(LicensesCodeEnum.ReviewAnalyticsLicence)?.type === LicenseTypeEnum.Full ||
                    getLicense(LicensesCodeEnum.ReviewAnalyticsLicence)?.type === LicenseTypeEnum.Competitor
                        ? getLicense(LicensesCodeEnum.ReviewAnalyticsLicence)
                        : {isActive: false},
                [FeaturesEnum.reviewAnalyticsFull]:
                    getLicense(LicensesCodeEnum.ReviewAnalyticsLicence)?.type === LicenseTypeEnum.Full ||
                    getLicense(LicensesCodeEnum.ReviewAnalyticsLicence)?.type === LicenseTypeEnum.Competitor
                        ? getLicense(LicensesCodeEnum.ReviewAnalyticsLicence)
                        : {isActive: false},
                [FeaturesEnum.reviewAnalyticsCompetitor]:
                    getLicense(LicensesCodeEnum.ReviewAnalyticsLicence)?.type === LicenseTypeEnum.Competitor
                        ? getLicense(LicensesCodeEnum.ReviewAnalyticsLicence)
                        : {isActive: false},
                [FeaturesEnum.duplicatesAndFakes]: getLicense(LicensesCodeEnum.FakesAndDuplicatesLicence),
                [FeaturesEnum.reviewsGenerator]: getLicense(LicensesCodeEnum.ReviewsGeneratorLicence),
                [FeaturesEnum.reviewSuggests]: getLicense(LicensesCodeEnum.AutoReviewLicence),
                [FeaturesEnum.storeLocator]: getLicense(LicensesCodeEnum.StorelocatorLicence),
                [FeaturesEnum.dashboard]: {
                    isActive: features.includes(FeaturesEnum.dashboard),
                },
                [FeaturesEnum.companyGroups]: getLicense(LicensesCodeEnum.CompanyGroupLicense),
                [FeaturesEnum.usedeskIntegration]: getLicense(LicensesCodeEnum.UsedeskIntegrationLicence),
                [FeaturesEnum.prices]: getLicense(LicensesCodeEnum.PricesLicence),
                [FeaturesEnum.analytics]: {
                    isActive: features.includes(FeaturesEnum.analytics),
                },
                [FeaturesEnum.tariffs]: {
                    isActive: features.includes(FeaturesEnum.tariffs),
                },
                [FeaturesEnum.userManagement]: getLicense(LicensesCodeEnum.RoleLicence),
                [FeaturesEnum.omnichannel]: getLicense(LicensesCodeEnum.OmnichannelLicence),
                [FeaturesEnum.publicApi]: getLicense(LicensesCodeEnum.PublicApiLicence),
                [FeaturesEnum.reviewGptReplySuggestions]: getLicense(LicensesCodeEnum.GptReplySuggestionsLicence),
                [FeaturesEnum.gptAutoReplyLicence]: getLicense(LicensesCodeEnum.GptReplySuggestionsLicence),
                [FeaturesEnum.mailingListLicence]: getLicense(LicensesCodeEnum.MailingListLicence),
                [FeaturesEnum.companiesExport]: {
                    isActive: features.includes(FeaturesEnum.companiesExport),
                },
                [FeaturesEnum.knowledge]: {
                    isActive: features.includes(FeaturesEnum.knowledge),
                },
                [FeaturesEnum.localPack]: getLicense(LicensesCodeEnum.LocalPackLicence),
                [FeaturesEnum.photoTool]: getLicense(LicensesCodeEnum.PhototoolLicence),
                [FeaturesEnum.onlinePresenceAnalytics]: getLicense(LicensesCodeEnum.OnlinePresenceAnalyticsLicence),
                [FeaturesEnum.posts]: getLicense(LicensesCodeEnum.PostsLicence),
                [FeaturesEnum.chatwoot]: getLicense(LicensesCodeEnum.ChatwootLicense),
                [FeaturesEnum.neuralNetwork]: {
                    isActive: user?.isDoubleGisUser ? Boolean(licensesGpt?.length) : false,
                },
                [FeaturesEnum.bulkGenerateButton]: getLicense(LicensesCodeEnum.InternalAgencyReviewsLicence),
            },
            refetch,
            isLoading,
        };
    }, [
        user?.id,
        user?.isDoubleGisUser,
        userFeatures?.features,
        isDomainFeatureEnabled,
        getLicense,
        licensesGpt?.length,
        refetch,
        isLoading,
    ]);

    if (isInitialLoading || isInitialLoadingGpt || isTariffsLoading) {
        return <Page.Loading titleLangKey="LOADING_THREE_POINTS" />;
    }

    return <LicenseContext.Provider value={providerValue}>{children}</LicenseContext.Provider>;
}
